import axios from 'axios';
import { VehicleProfile, VehicleProfileFormData } from '../types/Vehicle';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

class VehicleProfileService {
  private handleError(error: any): Error {
    if (error.response) {
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      return new Error(message);
    } else if (error.request) {
      return new Error('No response from server. Please check your connection.');
    } else {
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  async getAllProfiles(): Promise<VehicleProfile[]> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-profiles`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProfilesPaginated(page: number = 1, pageSize: number = 10, search?: string): Promise<{ data: VehicleProfile[]; pagination: any }> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-profiles`, {
        // V3-028: the list's search field, run on the server.
        params: { page, pageSize, ...(search?.trim() ? { search: search.trim() } : {}) }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProfileById(id: number): Promise<VehicleProfile> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-profiles/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProfileByInspection(inspection: string): Promise<VehicleProfile | null> {
    try {
      const profiles = await this.getAllProfiles();
      return profiles.find(p => p.inspection === inspection) || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createProfile(data: VehicleProfileFormData): Promise<VehicleProfile> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicle-profiles`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProfile(id: number, data: Partial<VehicleProfileFormData>): Promise<VehicleProfile> {
    try {
      const response = await axios.put(`${BASE_URL}/vehicle-profiles/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteProfile(id: number): Promise<void> {
    try {
      await axios.delete(`${BASE_URL}/vehicle-profiles/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehiclesUsingProfile(profileId: number): Promise<any[]> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-profiles/${profileId}/vehicles`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export default new VehicleProfileService();

